import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Input as InputAnt, type InputProps } from 'antd';
import { type ChangeEvent, type FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { toNumber } from 'src/helpers';

const APPROPRIATE_NUMBERS_REGEX = /^\d*\.?\d*$/g;

type NumberInputProps = InputProps & {
  showControlIcons?: boolean;
};

export function NumberInput({
  onChange,
  onBlur,
  value,
  defaultValue,
  showControlIcons,
  ...rest
}: Readonly<NumberInputProps>) {
  const internalDefaultValue = useMemo(
    () => (defaultValue != null && !Array.isArray(defaultValue) ? toNumber(defaultValue as string | number) : ''),
    [defaultValue]
  );

  const [internalValue, setInternalValue] = useState<number | ''>(
    internalDefaultValue !== '' ? internalDefaultValue : value != null && value !== '' ? toNumber(String(value)) : ''
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        const targetValue = event.target.value;

        if (targetValue && !targetValue.match(APPROPRIATE_NUMBERS_REGEX)) {
          return;
        }

        const appliedMask = {
          ...event,

          target: {
            ...event.target,

            value: (targetValue || null) as unknown as string,
          },
        };

        onChange(appliedMask);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      const targetValue = event.target.value;

      const numberedValue: ChangeEvent<HTMLInputElement> = {
        ...event,

        target: {
          ...event.target,

          value: (targetValue !== '' ? toNumber(targetValue) : null) as unknown as string,
        },
      };

      setInternalValue(targetValue != null && targetValue !== '' ? toNumber(targetValue) : '');
      onChange?.(numberedValue);

      onBlur?.(event);
    },
    [onChange, onBlur]
  );

  const handleDecrement = useCallback(() => {
    setInternalValue((prev) => (prev != null && prev !== '' ? (prev > 0 ? prev - 1 : prev) : 0));
  }, []);

  const handleIncrement = useCallback(() => {
    setInternalValue((prev) => (prev != null && prev !== '' ? prev + 1 : 0));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    if ((value === null || typeof value === 'string') && value !== String(internalValue)) {
      setInternalValue(value != null && value !== '' ? toNumber(value) : '');
    }
  }, [value]);

  return (
    <InputAnt
      {...rest}
      value={internalValue}
      prefix={showControlIcons && <MinusOutlined style={{ fontSize: 20 }} onClick={handleDecrement} />}
      suffix={showControlIcons && <PlusOutlined style={{ fontSize: 20 }} onClick={handleIncrement} />}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
